/**
 * ログイン状態のとりまとめ。
 *
 * ここが持っている決まりごと:
 *  - ログイン＝電話番号へ認証番号（6桁）を送り、届いた番号を入れて通行証を受け取る（決定33）
 *  - ログインしたら、ご自身の情報・会社の表示設定・役職の束をまとめて読み込む
 *  - 管理の「できること」を1つも持たない方は、この画面をお使いいただけない
 *  - 通行証の期限が切れたら、控えで取り直して使い続けていただく（取り直せなければログイン画面へ）
 *  - しばらく操作が無いとき（自動ログアウト）は、ログイン画面へ戻す
 *  - ログアウトするときは、サーバ側でも控えを使えなくしてから手元を消す（issue #318）
 *
 * ログインの窓口（`POST /auth/phone/request-code`・`POST /auth/phone/verify`）は、アプリと同じものを
 * 「管理画面から」の印（`purpose: 'admin'`）付きで使う。名簿に載っていない方・管理の仕事を持たない方は
 * サーバが番号の確認のあとでお断りする（名簿に載っているかどうかは、番号を送る時点では分からない）。
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiContext } from '../api/context.js';
import { ApiError, createApiClient, type ApiClient } from '../api/client.js';
import type { Branding, Me, PhoneCodeChallenge, RolesResponse, TokenPair } from '../api/types.js';
import { isAdminGrant, type Grant } from '../menu/visible.js';
import { rememberBrandColor } from '../theme/remembered.js';
import { IDLE_LIMIT_MS, isIdleExpired } from './idle.js';
import {
  clearSession,
  fromTokenPair,
  getAccessToken,
  getSession,
  restoreSession,
  setSession,
  subscribeSession,
  touchSession,
  userIdOfAccessToken,
} from './token-store.js';
import {
  SessionContext,
  type LoginChallenge,
  type SessionProfile,
  type SessionStatus,
  type SessionValue,
} from './session-context.js';

/** ログインの有効期限が切れたときの案内。 */
const EXPIRED_NOTICE = 'ログインの有効期限が切れました。もう一度ログインしてください。';
/** 自動ログアウトの案内。 */
const IDLE_NOTICE = 'しばらく操作がなかったため、自動でログアウトしました。';
/**
 * 開き直した瞬間にサーバへつながらなかったときの案内（issue #363）。ログインは残してある。
 */
const UNREACHABLE_NOTICE =
  'サーバにつながりませんでした。少し待ってから「もう一度読み込む」を押してください。';
/** ほかのタブでログアウトしたときの案内。 */
const OTHER_TAB_SIGNED_OUT_NOTICE =
  'ほかのタブでログアウトしたため、このタブもログアウトしました。';
/** 開き直したときに、つながらなかったら自動でやり直すまでの間（2回まで）。 */
const RESTORE_RETRY_DELAYS_MS = [1000, 3000];

/**
 * 開き直したときの失敗のうち、ログインを残してやり直すもの（issue #363）。
 * つながらなかった（0）・サーバの不調（5xx・混み合い）。ただし「管理の仕事の一覧を返さない」
 * （サーバの版が古い）は待っても直らないので含めない。
 */
function keepsSessionOnFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  if (error.code === 'server_too_old') return false;
  return error.status === 0 || error.status >= 500 || error.status === 429 || error.status === 408;
}

/** 管理画面をお使いいただけない方への案内。 */
const ADMIN_ONLY_MESSAGE = 'この画面をお使いいただけるのは、管理者の方だけです。';
/**
 * 役職の窓口が「管理の仕事の一覧」を返さなかったときの案内。
 * この一覧が無いと誰も入れなくなるので、**締め出しの理由が分かる言葉**にしておく
 * （画面とサーバの版がずれたときに、原因の分からない断られ方をしないように）。
 */
const SERVER_TOO_OLD_MESSAGE =
  'いまは管理画面をお使いいただけません（システムの更新が必要です）。システムの担当者へお知らせください。';
/** タブに出す既定の名前（`index.html` と同じ）。 */
const DEFAULT_DOCUMENT_TITLE = '管理画面';

/**
 * 会社の表示（名前・色・ロゴ）が読めなかったときの当て。
 * 見た目の情報なので、読めなくても管理の仕事は止めない（値の正はサーバの設定・docs/01 20-5）。
 */
const FALLBACK_BRANDING: Branding = { app_name: '管理画面', color_seed: '', logo_url: null };

/**
 * タブに出す名前。会社ごとのアプリ名を頭に付ける（`index.html` の「管理画面」を上書きする）。
 * 表示設定が読めなかったときは「管理画面」だけにする（会社名を推測で出さない・docs/01 20-5）。
 */
function documentTitleFor(branding: Branding): string {
  const name = branding.app_name.trim();
  return name === '' || name === DEFAULT_DOCUMENT_TITLE
    ? DEFAULT_DOCUMENT_TITLE
    : `${name} ${DEFAULT_DOCUMENT_TITLE}`;
}

/** 操作しているかを見に行く間隔（自動ログアウトの確認）。 */
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;

export interface SessionProviderProps {
  children: ReactNode;
  /** APIの呼び先（既定は `/api`）。テストから差し替える */
  apiBaseUrl?: string;
  /** 通信の実装（テストから差し替える） */
  fetchImpl?: typeof fetch;
}

export function SessionProvider({
  children,
  apiBaseUrl,
  fetchImpl,
}: SessionProviderProps): ReactNode {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [profile, setProfile] = useState<SessionProfile | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // ログアウトの知らせをサーバへ送る窓口（下で作る api を後から差し込む）
  const revokeRef = useRef<(refreshToken: string) => void>(() => undefined);

  /**
   * 手元の通行証を消す。控えがあれば、先にサーバへ「もう使わない」と知らせる（issue #318）。
   * 知らせは**結果を待たない**（届かなくてもログアウトは止めない・待たせない）。
   * 共用のパソコンで写しが残っても、サーバ側で使えなくなっていれば取り直しに使えない。
   */
  const dropSession = useCallback(() => {
    const refreshToken = getSession()?.refreshToken;
    if (refreshToken) revokeRef.current(refreshToken);
    clearSession();
  }, []);

  const signOut = useCallback(
    (message?: string) => {
      dropSession();
      setProfile(null);
      setStatus('signed_out');
      setNotice(message ?? null);
      // タブの名前も元に戻す。共用のパソコンで、次にお使いになる方に
      // 前の会社の名前が残って見えないようにする（issue #184 L-2）
      document.title = DEFAULT_DOCUMENT_TITLE;
    },
    [dropSession],
  );

  // 401 を受け取ったときに呼ぶ先。窓口を作り直さずに済むよう、間に1つ挟んでいる
  const signOutRef = useRef(signOut);
  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  const api = useMemo<ApiClient>(
    () =>
      createApiClient({
        ...(apiBaseUrl !== undefined ? { baseUrl: apiBaseUrl } : {}),
        ...(fetchImpl !== undefined ? { fetchImpl } : {}),
        getToken: getAccessToken,
        // 通行証の期限（既定15分）が切れても、控えで取り直せる限りは使い続けていただく。
        // 最後に操作した時刻は引き継ぐ（取り直しで自動ログアウトの時計が戻らないように）
        getRefreshToken: () => getSession()?.refreshToken ?? null,
        onRefreshed: (tokens) =>
          setSession({
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            lastActiveAt: getSession()?.lastActiveAt ?? Date.now(),
          }),
        onUnauthorized: () => signOutRef.current(EXPIRED_NOTICE),
      }),
    [apiBaseUrl, fetchImpl],
  );

  useEffect(() => {
    revokeRef.current = (refreshToken: string) => {
      // 通行証を付けずに送る（期限切れで弾かれて、また自動ログアウトの流れに入らないように）
      api
        .post('/auth/logout', {
          body: { refresh_token: refreshToken },
          anonymous: true,
          keepSessionOnUnauthorized: true,
        })
        .catch(() => {
          // 届かなくても手元は消してある。控えはサーバ側の期限で使えなくなる
        });
    };
  }, [api]);

  const loadProfile = useCallback(async (): Promise<SessionProfile> => {
    const [me, branding, roles] = await Promise.all([
      api.get<Me>('/me'),
      // 会社の表示（名前・色）は見た目だけの情報なので、読めなくても画面は開けるようにする
      // （見た目が読めないだけで管理の仕事が止まる、を避ける）
      api.get<Branding>('/org/branding').catch(() => FALLBACK_BRANDING),
      api.get<RolesResponse>('/org/roles'),
    ]);
    const role = roles.roles.find((row) => row.key === me.role);
    // 「管理の仕事にあたるできること」の一覧はサーバが返すものをそのまま使う。
    // 画面側で同じ一覧を持たない（持つと片方だけ直したときに食い違う・issue #159）。
    // 返ってこないときは、推測で通さず**理由の分かる言葉で**お断りする（issue #184 L-9）
    if (roles.management_capabilities === undefined) {
      throw new ApiError(500, 'server_too_old', SERVER_TOO_OLD_MESSAGE);
    }
    const managementCapabilities = roles.management_capabilities;
    // 役職の行が見つからないときは「何もできない束」として扱う（推測で権限を与えない）
    const verificationSmsOutbox = roles.verification_sms_outbox === true;
    const grant: Grant = role
      ? {
          role: me.role,
          capabilities: role.capabilities,
          scope: role.scope,
          managementCapabilities,
          verificationSmsOutbox,
        }
      : { capabilities: {}, scope: 'site', managementCapabilities, verificationSmsOutbox };
    if (!isAdminGrant(grant)) {
      throw new ApiError(403, 'admin_only', ADMIN_ONLY_MESSAGE);
    }
    // タブの名前を会社のアプリ名に合わせる（index.html の既定を上書きする）
    document.title = documentTitleFor(branding);
    // ログインの画面を会社の色で出すため、読めた色をこの端末に覚える（総点検 M1）。
    // 設定を読めなかったとき（当ての値）は、覚えた色を消さないよう何もしない
    if (branding !== FALLBACK_BRANDING) rememberBrandColor(branding.color_seed);
    return {
      me,
      branding,
      roles,
      grant,
      roleDisplayName: role?.display_name ?? '',
    };
  }, [api]);

  const requestLoginCode = useCallback(
    async (phone: string): Promise<LoginChallenge> => {
      const answer = await api.post<PhoneCodeChallenge>('/auth/phone/request-code', {
        body: { phone: phone.trim(), purpose: 'admin' },
        anonymous: true,
        keepSessionOnUnauthorized: true,
      });
      return {
        challenge: answer.challenge,
        phoneHint: answer.phone_hint,
        expiresIn: answer.expires_in,
        resendAfter: answer.resend_after,
      };
    },
    [api],
  );

  const verifyLoginCode = useCallback(
    async (challenge: string, code: string): Promise<void> => {
      const answer = await api.post<TokenPair & { result?: string }>('/auth/phone/verify', {
        body: { challenge, code: code.trim() },
        anonymous: true,
        // ここでの失敗は「入れ直していただく」場面。ログアウト扱いにはしない
        keepSessionOnUnauthorized: true,
      });
      // 管理画面の入口では、名簿に無い方は申し込みへ進まずお断りになる（サーバが 403 で返す）。
      // 念のため、通行証が入っていない答えはログインできなかったものとして扱う
      if (answer.result !== 'signed_in' || typeof answer.access_token !== 'string') {
        throw new ApiError(403, 'admin_only', ADMIN_ONLY_MESSAGE);
      }
      setSession(fromTokenPair(answer));
      try {
        const loaded = await loadProfile();
        setProfile(loaded);
        setNotice(null);
        setStatus('signed_in');
      } catch (error) {
        // 入れなかったときは通行証を残さない（サーバ側でも使えなくする）
        dropSession();
        setProfile(null);
        setStatus('signed_out');
        throw error;
      }
    },
    [api, loadProfile, dropSession],
  );

  /**
   * 開き直したときの読み込みを、何回目か。「もう一度読み込む」・ほかのタブでのログインで増やし、
   * 下の復帰をやり直す。
   */
  const [restoreAttempt, setRestoreAttempt] = useState(0);

  // 画面を読み込み直したとき・新しいタブで開いたときの復帰
  useEffect(() => {
    const restored = restoreSession();
    if (!restored) {
      setStatus('signed_out');
      return;
    }
    if (isIdleExpired(restored.lastActiveAt, Date.now(), IDLE_LIMIT_MS)) {
      dropSession();
      setStatus('signed_out');
      setNotice(IDLE_NOTICE);
      return;
    }
    let cancelled = false;
    let timer: number | undefined;
    const attempt = (tries: number): void => {
      loadProfile()
        .then((loaded) => {
          if (cancelled) return;
          setProfile(loaded);
          setNotice(null);
          setStatus('signed_in');
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (keepsSessionOnFailure(error)) {
            // 通信の瞬断・サーバの入れ替え中など、待てば直る失敗。ログインは消さない（issue #363）。
            // 少し間をおいて自動でやり直し、それでもだめなら「もう一度読み込む」をお出しする
            if (tries < RESTORE_RETRY_DELAYS_MS.length) {
              timer = window.setTimeout(() => attempt(tries + 1), RESTORE_RETRY_DELAYS_MS[tries]);
              return;
            }
            setProfile(null);
            setStatus('unreachable');
            setNotice(UNREACHABLE_NOTICE);
            return;
          }
          // 401（期限切れ・無効）と 403（管理の束を持たない方）などは、ログインからやり直していただく
          const status = error instanceof ApiError ? error.status : 0;
          const message =
            error instanceof ApiError && status !== 401 ? error.message : EXPIRED_NOTICE;
          dropSession();
          setProfile(null);
          setStatus('signed_out');
          setNotice(message);
        });
    };
    attempt(0);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [loadProfile, dropSession, restoreAttempt]);

  const retryRestore = useCallback(() => {
    setStatus('loading');
    setNotice(null);
    setRestoreAttempt((n) => n + 1);
  }, []);

  // 同じブラウザのほかのタブでのログイン・ログアウトを受け取る（issue #363）
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  // いま画面に出している方（名前・メニュー）。置き場が別の方に入れ替わったかを見分ける（issue #394）
  const profileRef = useRef(profile);
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);
  useEffect(
    () =>
      subscribeSession((session) => {
        if (session === null && statusRef.current === 'signed_in') {
          // ほかのタブでログアウトした（または自動ログアウトした）。控えはそちらで使えなくしてある
          setProfile(null);
          setStatus('signed_out');
          setNotice(OTHER_TAB_SIGNED_OUT_NOTICE);
          document.title = DEFAULT_DOCUMENT_TITLE;
          return;
        }
        if (session !== null && statusRef.current !== 'signed_in') {
          // ほかのタブでログインした。このタブも同じログインで開く
          retryRestore();
          return;
        }
        if (session !== null && statusRef.current === 'signed_in') {
          // ログインしたままの間に、置き場が別の方のログインに入れ替わった（issue #394）。
          // 名前・メニュー・受け持ちの範囲を前の方のまま、操作だけ別の方として送られないよう、
          // その場で読み込み直す（同じ方の通行証の取り直しでは、何もしない）
          const shown = profileRef.current?.me.user_id ?? null;
          const stored = userIdOfAccessToken(session.accessToken);
          if (shown !== null && stored !== null && stored !== shown) retryRestore();
        }
      }),
    [retryRestore],
  );

  // 自動ログアウト（しばらく操作が無いとき）
  useEffect(() => {
    if (status !== 'signed_in') return;
    const markActive = (): void => touchSession();
    window.addEventListener('click', markActive);
    window.addEventListener('keydown', markActive);
    const timer = window.setInterval(() => {
      const current = getSession();
      if (!current) return;
      if (isIdleExpired(current.lastActiveAt, Date.now(), IDLE_LIMIT_MS)) {
        signOut(IDLE_NOTICE);
      }
    }, IDLE_CHECK_INTERVAL_MS);
    return () => {
      window.removeEventListener('click', markActive);
      window.removeEventListener('keydown', markActive);
      window.clearInterval(timer);
    };
  }, [status, signOut]);

  const value: SessionValue = useMemo(
    () => ({
      status,
      profile,
      notice,
      requestLoginCode,
      verifyLoginCode,
      signOut,
      retryRestore,
    }),
    [status, profile, notice, requestLoginCode, verifyLoginCode, signOut, retryRestore],
  );

  return (
    <ApiContext.Provider value={api}>
      <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
    </ApiContext.Provider>
  );
}
